/*
  <globo-options-popup> — moves the Globo customization form into a popup that
  opens from the Add to cart button, the sticky ATC, or a Globo promo block
  (see snippet product-globo-promo.liquid). Markup + scoped CSS live in
  snippet globo-options-popup.liquid, which passes config via data attributes:
    data-section-id : the product section id
    data-form-id    : the id of the product <form>

  The one thing to know before touching this file: Globo does NOT bind to the
  theme's add-to-cart button. It CLONES it -- the original stays in the form but
  hidden (.gpo-original-atc-button) and Globo carries its add-to-cart takeover on
  the clone (.gpo-clone-atc-button). That takeover is what posts the add-on
  products as their own cart lines, in a single JSON /cart/add.js with an items[]
  array. The theme's own handler cannot do that: it serialises the form and adds
  one item, so an add-on going through it survives only as a line item property,
  with no product and no price. Hence atcButton() resolves the target at click
  time instead of trusting the reference captured at startup.

  Because Globo owns that add, the theme never hears about it: with the app's
  "Go to cart immediately after adding to cart" option off, nothing on the page
  moves and the cart drawer stays stale until a reload. installCartAddWatcher()
  below watches the network for the /cart/add Globo fires and hands the refresh
  back to the theme's own window.refreshCart().

  The second thing to know: the app answers LATE. It is a deferred
  theme-app-extension script, so it cannot start before the whole document is
  parsed, and it then pulls five more chunks off the CDN and awaits a Storefront
  call before it renders -- one to three seconds on a real connection. The
  add-to-cart button is painted and clickable that whole time. See the "Globo
  load window" section in init(): rather than let a click through and lose the
  add-ons, the click is held until Globo has said whether this product has
  options at all.
*/
if (typeof GloboOptionsPopup !== 'function') {
  // Globo posts its own /cart/add.js -- main product and add-ons in a single
  // items[] array -- from handlers we do not own. With its "Go to cart
  // immediately after adding to cart" option ON, the app then redirects to
  // /cart and the customer sees the result. With it OFF it simply leaves the
  // page untouched: the theme never learns the cart changed, so the drawer
  // keeps showing the markup Liquid rendered at page load and only catches up
  // on a reload. The app exposes no completion event we can rely on, so the
  // add is observed where it is guaranteed to show up -- the network.
  // Installed once per page, on the first add started from a popup; it only
  // re-broadcasts the completion as a 'gpo:cart-added' event on document and
  // leaves every decision to the popup instances.
  function installCartAddWatcher() {
    if (window.__gpoCartAddWatcher) return;
    window.__gpoCartAddWatcher = true;

    // fetch() takes a string, a URL or a Request; XHR.open() a string or URL.
    function urlOf(input) {
      if (!input) return '';
      if (typeof input === 'string') return input;
      if (typeof input.url === 'string') return input.url;   // Request
      if (typeof input.href === 'string') return input.href; // URL
      try { return String(input); } catch (e) { return ''; }
    }
    // Matches /cart/add and /cart/add.js, absolute or relative, and whatever
    // locale prefix the storefront runs under (/fr/cart/add.js).
    function isCartAdd(input) {
      return urlOf(input).split('?')[0].indexOf('/cart/add') !== -1;
    }

    function announce() {
      document.dispatchEvent(new Event('gpo:cart-added'));
    }

    // Fired synchronously the moment a /cart/add leaves the page, before any
    // response. That is the only reliable "the add was really attempted"
    // signal: Globo validates its required options inside the click itself and,
    // when one is missing, renders its error and posts nothing at all. The
    // popup uses that to tell a refusal from an add in flight (delegateAdd).
    function announceStart() {
      document.dispatchEvent(new Event('gpo:cart-add-started'));
    }

    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input) {
        var watched = isCartAdd(input);
        if (watched) announceStart();
        var request = nativeFetch.apply(this, arguments);
        if (request && typeof request.then === 'function' && watched) {
          request.then(function (response) {
            // 422 (sold out, invalid options) comes back as ok === false: the
            // cart did not change, so there is nothing to refresh.
            if (!response || response.ok) announce();
          // The rejection handler is mandatory: observing a request must never
          // turn its failure into an unhandled rejection of its own.
          }, function () {});
        }
        return request;
      };
    }

    // Kept alongside fetch because the app is free to use either, and a jQuery
    // ajax call (which several Shopify apps still ship) lands here.
    var xhr = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (xhr && xhr.open && xhr.send) {
      var nativeOpen = xhr.open;
      var nativeSend = xhr.send;
      xhr.open = function (method, url) {
        try { this.__gpoCartAdd = isCartAdd(url); } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };
      xhr.send = function () {
        if (this.__gpoCartAdd) {
          announceStart();
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) announce();
          });
        }
        return nativeSend.apply(this, arguments);
      };
    }
  }

  // Page-level record that Globo has finished, kept outside the element so it
  // survives it. Registered once, when this file first runs -- which is always
  // before the app can have rendered, since its script is deferred too and then
  // awaits its chunks. A <globo-options-popup> created later (a section
  // re-render, a quick buy) reads this instead of opening a load window that is
  // long over and holding clicks for a verdict that already came in.
  document.addEventListener('gpoRenderCompleted', function () {
    window.__gpoSettled = true;
  });

  class GloboOptionsPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      this.init();
    }

    init() {
      var popup = this;
      var SECTION_ID = this.dataset.sectionId;
      var FORM_ID = this.dataset.formId;

      // In the theme editor, reveal Globo promo blocks immediately so they can be
      // configured even on products without a Globo form (they are hidden via CSS
      // until body[data-globo-ready] otherwise).
      if (window.Shopify && window.Shopify.designMode) {
        try { document.body.setAttribute('data-globo-ready', ''); } catch (e) {}
      }

      var form = document.getElementById(FORM_ID);
      var dialog = popup.querySelector('[data-globo-popup-dialog]');
      var body = popup.querySelector('[data-globo-popup-body]');
      var nativeBtn = form && form.querySelector('[data-main-product-add-to-cart]');
      var addBtn = popup.querySelector('[data-globo-popup-add]');
      var errorBox = popup.querySelector('[data-globo-popup-error]');
      var lastFocus = null;

      // Flipped on by the cart-add watcher as soon as a /cart/add leaves the
      // page. delegateAdd() resets it just before handing the add over, then
      // reads it back synchronously to know whether Globo accepted or refused.
      var addStarted = false;
      document.addEventListener('gpo:cart-add-started', function () { addStarted = true; });

      // Becomes true only when an actual Globo customization form is detected on
      // this product. Until then the popup stays completely inert so non-Globo
      // products keep the normal direct add-to-cart behaviour.
      var gpoActive = false;

      // True only while we re-dispatch a click on the real add-to-cart button
      // from inside the popup. Both interceptors below bail out on it, so that
      // synthetic click reaches the theme's and Globo's handlers untouched.
      var bypass = false;

      // ---- Globo load window ------------------------------------------------
      // Globo renders one to three seconds after the add-to-cart button is
      // already clickable (see the header comment). A click in that window used
      // to fall straight through to the theme's plain single-item add: the
      // customer got the product with none of the add-ons, and no popup ever
      // appeared. So while the answer is still unknown the click is HELD.
      //
      // gpoSettled is the moment Globo has spoken. It is driven by
      // 'gpoRenderCompleted', which the app dispatches on document for EVERY
      // product -- with options and without, since its no-options branch removes
      // its own container and dispatches anyway. That makes it a verdict rather
      // than just a success signal, which is exactly what a gate needs.
      //
      // Nothing is ever held in the theme editor: Globo does not necessarily
      // render there, and a button that swallows clicks would just look broken.
      var gpoSettled = !!window.__gpoSettled ||
                       !!(window.Shopify && window.Shopify.designMode);
      var queuedBtn = null; // the button whose click is being held, if any
      var queuedTimer = null;

      // Cap on how long a held click waits before it is released as a normal
      // add. Only ever reached when the app fails to load at all (blocked by an
      // extension, offline, uninstalled).
      var QUEUE_TIMEOUT = 5000;
      // Hard end of the load window. Past it both buttons behave exactly as they
      // did before this file existed, so a late click is never held.
      var SETTLE_TIMEOUT = 10000;

      // Whether Globo can still be expected to render. Its config object is
      // absent entirely when the app embed is off, so nothing is ever held on a
      // store or a page without Globo.
      function globoPending() {
        return !gpoSettled && !!(window.GPOConfigs && window.GPOConfigs.appEmbed);
      }

      // Tells the promo block to show its skeleton instead of nothing
      // (component-globo-promo.css). Deliberately set from JS rather than
      // assumed by CSS: when this script does not run at all, the block keeps
      // its old behaviour of staying hidden rather than being stuck shimmering.
      if (globoPending()) {
        try { document.body.setAttribute('data-globo-loading', ''); } catch (e) {}
      }

      var GPO_SELECTOR = '.globo-product-options, .gpo-form, .gpo-container, [data-gpo-options]';
      var gpoEl = null; // the Globo options container once detected/relocated
      var gpoHome = null; // where the app originally rendered it

      function isOpen() { return !!(dialog && dialog.open); }

      // Activate popup mode: the native button stops submitting and the popup's
      // button becomes the real submit (so the loader shows on it).
      function activate() {
        if (gpoActive) return;
        gpoActive = true;
        // Nothing is neutralised here on purpose: the add-to-cart button keeps
        // its type and its listeners so the add can be handed back to it (or
        // rather to Globo's clone of it, see atcButton) exactly as it would run
        // without this popup. The popup's own button delegates instead of
        // submitting the form, which only ever added the main product.
        // Reveal any Globo promo blocks (see snippet product-globo-promo.liquid),
        // which stay hidden via CSS until a Globo form is confirmed present.
        try { document.body.setAttribute('data-globo-ready', ''); } catch (e) {}
      }

      // Globo does not bind to the theme's add-to-cart button: it CLONES it. The
      // original is kept in the form but hidden (.gpo-original-atc-button) and
      // Globo carries its add-to-cart takeover on the clone
      // (.gpo-clone-atc-button) -- the clone is what posts the add-on products as
      // their own cart lines. The clone only exists once the app has rendered, so
      // the button MUST be resolved at click time: the reference captured in
      // init() is the hidden original, and clicking it fell straight through to
      // the theme's plain single-item add (the add-on then survived only as a
      // line item property, with no product and no price).
      function atcButton() {
        var clone = (form && form.querySelector('.gpo-clone-atc-button')) ||
                    document.querySelector('#product-' + SECTION_ID + ' .gpo-clone-atc-button');
        return clone || nativeBtn;
      }

      function isDisabled(btn) {
        return !btn || btn.disabled ||
          btn.classList.contains('disabled') ||
          btn.getAttribute('aria-disabled') === 'true';
      }

      function showPopupError() { if (errorBox) errorBox.hidden = false; }
      function hidePopupError() { if (errorBox) errorBox.hidden = true; }

      // Best effort only, used to scroll the popup to the app's own message
      // once it is back on screen. The class names belong to Globo, so the
      // list is deliberately loose and finding nothing is not a failure.
      var GPO_ERROR_SELECTOR =
        '.gpo-error, .globo-error, .gpo-message-error, .gpo-invalid, [class*="error"]';
      function firstVisibleError() {
        if (!body) return null;
        var nodes = body.querySelectorAll(GPO_ERROR_SELECTOR);
        for (var i = 0; i < nodes.length; i++) {
          // offsetParent is null on display:none, which is how the app keeps
          // its message nodes around while the field is still valid.
          if (nodes[i].offsetParent !== null) return nodes[i];
        }
        return null;
      }

      function clearWorking() {
        document.querySelectorAll(
          '[data-main-product-add-to-cart].working, sticky-add-to-cart [data-js-atc].working'
        ).forEach(function (b) { b.classList.remove('working'); });
        if (addBtn) addBtn.classList.remove('working');
      }

      // Hold the add: show the theme's own button loader, so the click visibly
      // registered, and remember the intent. Marking the native button lights
      // both buttons up -- the sticky bar mirrors .working off it with its own
      // MutationObserver (component-product-form.js).
      function queueAdd(btn) {
        queuedBtn = btn;
        (nativeBtn || btn).classList.add('working');
        if (queuedTimer) clearTimeout(queuedTimer);
        // Give up on Globo entirely rather than leave the customer with a button
        // that eats clicks.
        queuedTimer = setTimeout(settle, QUEUE_TIMEOUT);
      }

      // Globo has spoken (or we gave up waiting): act on the held click.
      function releaseQueuedAdd() {
        var btn = queuedBtn;
        queuedBtn = null;
        if (queuedTimer) { clearTimeout(queuedTimer); queuedTimer = null; }
        if (!btn) return;
        if (gpoActive) { openPopup(); return; } // openPopup() clears the loader
        clearWorking();
        // No Globo form on this product after all: replay the click so the add
        // takes the theme's normal path. A click that came from the sticky bar
        // is replayed on the native button, which is exactly what the theme's
        // own sticky forwarding would have done with it.
        var target = btn.hasAttribute('data-main-product-add-to-cart')
          ? btn
          : (nativeBtn || btn);
        if (isDisabled(target)) return;
        bypass = true;
        try {
          target.click();
        } finally {
          // Released on the next tick, like delegateAdd(): the resulting submit
          // is dispatched synchronously inside the click above.
          setTimeout(function () { bypass = false; }, 0);
        }
      }

      // The single point where the load window ends, whichever way it ends.
      function settle() {
        if (gpoSettled) return;
        gpoSettled = true;
        window.__gpoSettled = true; // covers the give-up path too
        relocate(); // last chance to detect the form before deciding
        // Drops the promo block's skeleton. If a form was found, activate() has
        // already set data-globo-ready and the real card shows; if not, the
        // block goes back to being hidden.
        try { document.body.removeAttribute('data-globo-loading'); } catch (e) {}
        releaseQueuedAdd();
      }

      // Detect the Globo form, activate popup mode, and move it into the popup.
      function relocate() {
        if (!body) return;
        var gpo = (form && form.querySelector(GPO_SELECTOR)) ||
                  document.querySelector('#product-' + SECTION_ID + ' ' + GPO_SELECTOR);
        if (gpo) {
          gpoEl = gpo;
          // Record the app's own position the first time we see the form, while
          // it is still there.
          if (!gpoHome && gpo.parentNode !== body) {
            gpoHome = { parent: gpo.parentNode, next: gpo.nextSibling };
          }
          hideAtHome();
          activate();
        }
      }

      // The form now stays in its original DOM position until the popup opens
      // (Globo binds its add-to-cart takeover there and must find it there), so
      // it would otherwise show up inline on the page as well. Hide it visually
      // while it sits at home; the inputs keep being serialized either way.
      function hideAtHome() {
        if (gpoEl && gpoEl.parentNode !== body) gpoEl.style.display = 'none';
      }
      function showGpo() {
        if (gpoEl) gpoEl.style.display = '';
      }

      // Move the Globo form into the popup. Deliberately NOT done on detection:
      // Globo wires its add-to-cart takeover when it finishes rendering, and it
      // binds against wherever its form sits at that moment. Moving the form
      // before that made Globo bind inside the popup, and it then never posted
      // the add-on products as their own cart lines -- the theme's own handler
      // did a plain single-item add instead and the add-on survived only as a
      // line item property. So the form stays home until the popup is opened.
      function moveIntoPopup() {
        if (!body || !gpoEl || gpoEl.parentNode === body) return;
        if (!gpoHome) gpoHome = { parent: gpoEl.parentNode, next: gpoEl.nextSibling };
        body.appendChild(gpoEl);
        showGpo();
      }

      // Put the Globo form back exactly where the app rendered it, hidden.
      function restoreGpo() {
        if (!gpoEl || !gpoHome || !gpoHome.parent) return;
        if (gpoEl.parentNode !== gpoHome.parent) {
          var next = gpoHome.next;
          if (next && next.parentNode === gpoHome.parent) {
            gpoHome.parent.insertBefore(gpoEl, next);
          } else {
            gpoHome.parent.appendChild(gpoEl);
          }
        }
        // Hidden unconditionally, not just after a move: the invariant is "at
        // home => not visible". delegateAdd() breaks it on purpose for the
        // duration of the add (restoreGpo + showGpo), so the form can already be
        // home AND visible when the deferred 'close' teardown gets here -- an
        // early return on "already home" left it on display on the page.
        // hideAtHome() tests the current parent, so it is a no-op while the form
        // is still in the popup, which is why it comes after the move.
        hideAtHome();
      }

      function openPopup() {
        if (!dialog || isOpen()) return;
        relocate();
        moveIntoPopup();
        clearWorking();
        lastFocus = document.activeElement;
        // showModal() promotes the dialog to the top layer, which is positioned
        // against the viewport and paints above every stacking context — the
        // product page nests this snippet inside transformed/positioned
        // ancestors that would otherwise clip and bury a position:fixed overlay.
        if (dialog.showModal) { dialog.showModal(); } else { dialog.setAttribute('open', ''); }
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        var add = popup.querySelector('[data-globo-popup-add]');
        if (add && !isDisabled(add)) { try { add.focus(); } catch (e) {} }
      }

      function closePopup() {
        if (!dialog || !isOpen()) return;
        if (dialog.close) { dialog.close(); } else { dialog.removeAttribute('open'); teardown(); }
      }

      // Cleanup lives on the dialog's 'close' event so that the native Escape
      // dismissal — which bypasses closePopup() entirely — still restores the
      // page scroll and the focus.
      function teardown() {
        // <dialog>.close() fires 'close' from a QUEUED task, so it can land
        // after a re-open: delegateAdd() closes, hands the add to Globo, and
        // reopens immediately when Globo refuses it. Restoring the form here
        // would then yank it straight back out of the popup we just reopened.
        if (isOpen()) return;
        // Hand the form back to the app as soon as the popup is gone, so Globo
        // always finds it at home outside of the popup being open.
        restoreGpo();
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        clearWorking();
        hidePopupError();
        if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
      }
      if (dialog) dialog.addEventListener('close', teardown);

      // Detect the Globo form: try once now (in case it already rendered), on
      // Globo's render event, and via a MutationObserver that fires only on real
      // DOM changes and disconnects as soon as the form is found.
      relocate();
      document.addEventListener('gpoRenderCompleted', relocate);
      // Registered after relocate() so the form is already detected by the time
      // settle() decides what to do with a held click.
      document.addEventListener('gpoRenderCompleted', settle);
      // Safety net: if the app never renders at all, the window still has to
      // close on its own or a held click would be held forever.
      setTimeout(settle, SETTLE_TIMEOUT);

      if (!gpoActive && typeof MutationObserver !== 'undefined') {
        var target = document.getElementById('product-' + SECTION_ID) || form || document.body;
        var observer = new MutationObserver(function () {
          relocate();
          if (gpoActive) observer.disconnect();
        });
        observer.observe(target, { childList: true, subtree: true });
        // Safety: stop observing on non-Globo products once the load window ends.
        setTimeout(function () { if (!gpoActive) observer.disconnect(); }, 15000);
      }

      // Open the popup when the native ATC button is clicked (Globo products only).
      // Delegated on document in the CAPTURE phase so we run BEFORE the theme's
      // <product-form> click handler (which AJAX-adds and stops propagation). A
      // listener attached directly to the button runs in the bubble phase and
      // would never fire once the theme stops propagation.
      //
      // The sticky ATC is matched explicitly rather than relying on the theme
      // forwarding its click to the native button (component-product-form.js):
      // that forwarding is a bubble-phase listener doing nativeBtn.click(), which
      // is a no-op whenever the native button is disabled (sold out, or no
      // variant chosen yet).
      document.addEventListener('click', function (e) {
        if (bypass) return; // our own re-dispatch: let it through to Globo
        var atc = e.target.closest('[data-main-product-add-to-cart]');
        if (atc) {
          if (form && !form.contains(atc)) return; // only this section's ATC
        } else {
          var stickyBtn = e.target.closest('sticky-add-to-cart [data-js-atc]');
          if (!stickyBtn) return;
          var bar = stickyBtn.closest('sticky-add-to-cart');
          if (bar && bar.dataset.id !== 'product-' + SECTION_ID) return;
        }
        relocate(); // detect + activate Globo on demand
        // The native button is the authority on sold-out / choose-variant states;
        // the sticky one only mirrors them. Checked first so a button the theme
        // has already refused is never held either.
        if (isDisabled(atc || atcButton())) return;
        // Globo has not answered yet: hold the click rather than let it become
        // an add without the add-ons. Held even when a Globo form is already in
        // the DOM, because the app binds its add-to-cart takeover only at
        // gpoRenderCompleted and moveIntoPopup() before that made it bind inside
        // the popup (see the comment on moveIntoPopup).
        if (globoPending() && (atc || nativeBtn)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          queueAdd(atc || nativeBtn);
          return;
        }
        if (!gpoActive) return; // no Globo form -> let the normal add proceed
        e.preventDefault();
        e.stopImmediatePropagation();
        openPopup();
      }, true);

      // Guard form submission so the add can only happen from inside the open
      // popup (blocks Enter-key submits and any other bypass). Inert on non-Globo
      // products. Capture on document => runs before the theme's submit handler.
      document.addEventListener('submit', function (e) {
        if (bypass) return; // submit caused by our re-dispatch: let it through
        if (e.target !== form) return;
        relocate(); // detect + activate Globo on demand before deciding
        // Same hold as the click above: an Enter keypress during the load window
        // must not become a plain add either.
        if (globoPending() && nativeBtn && !isDisabled(nativeBtn)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          queueAdd(nativeBtn);
          return;
        }
        if (!gpoActive) return; // non-Globo product: normal direct add
        e.preventDefault();
        e.stopImmediatePropagation();
        // An Enter keypress inside a Globo field submits the form directly. Left
        // to the theme's handler it would add the main product without the
        // add-ons, so route it through the same delegation as the add button.
        if (isOpen()) { delegateAdd(); return; }
        openPopup();
      }, true);

      // Open the popup from a Globo promo block (data-globo-popup-open). relocate()
      // activates popup mode + moves the Globo form in; we only open if a Globo
      // form is actually present (gpoActive), so the trigger is inert otherwise.
      function openFromTrigger(e) {
        e.preventDefault();
        relocate();
        if (gpoActive) openPopup();
      }
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-globo-popup-open]')) openFromTrigger(e);
      });
      // Keyboard support for the div[role="button"] trigger (Enter / Space).
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        if (e.target.closest('[data-globo-popup-open]')) openFromTrigger(e);
      });

      // ---- Cart drawer sync -------------------------------------------------
      // Only armed for an add started from this popup, and only until it lands
      // (or the deadline passes), so the watcher above stays inert for every
      // other /cart/add on the page -- a quick-add from a product card keeps
      // being handled by the theme alone.
      var cartSyncTimer = null;
      var cartSyncDeadline = 0; // epoch ms; 0 means not armed
      var cartSyncDelay = 0;

      // themeMayClaim: the add went to the theme's own button rather than to
      // Globo's clone, so the theme's <product-form> is going to handle it and
      // we have to leave it the time to say so (see the delay below).
      function armCartSync(themeMayClaim) {
        installCartAddWatcher();
        cartSyncDeadline = Date.now() + 15000;
        cartSyncDelay = themeMayClaim ? 600 : 150;
      }

      // The theme claimed the add itself: its <product-form> handler already
      // re-rendered the drawer, updated the header count and opened the drawer
      // (section-main-product.js). Ours would only fetch the section twice.
      function cancelCartSync() {
        cartSyncDeadline = 0;
        if (cartSyncTimer) { clearTimeout(cartSyncTimer); cartSyncTimer = null; }
      }

      document.addEventListener('gpo:cart-added', function () {
        if (!cartSyncDeadline || Date.now() > cartSyncDeadline) return; // not ours
        cartSyncDeadline = 0;
        if (cartSyncTimer) clearTimeout(cartSyncTimer);
        // Delayed rather than immediate: just enough for Globo to settle its
        // own DOM, and -- when the add could still be the theme's (see
        // armCartSync) -- long enough for its 'add-to-cart' to cancel us, since
        // the theme only fires it once its own helper-cart fetch has come back.
        cartSyncTimer = setTimeout(function () {
          cartSyncTimer = null;
          clearWorking();
          // Normally already closed by delegateAdd. This only matters if the
          // add came back after refuseAdd() reopened the popup, i.e. the app
          // validated asynchronously and posted later than our check.
          closePopup();
          // The theme's own hook for apps that add to the cart behind its back
          // (component-cart.js): re-renders the drawer from section helper-cart,
          // refreshes the header count/total, regenerates the recommendations
          // and opens the drawer. #AjaxCartForm only exists where that drawer is
          // rendered -- not on the cart template, where nothing needs syncing.
          if (typeof window.refreshCart === 'function' && document.getElementById('AjaxCartForm')) {
            window.refreshCart();
          }
        }, cartSyncDelay);
      });

      // Popup "Add to cart": hand the add back to the NATIVE button rather than
      // submitting the form ourselves. Globo binds its handlers to that button,
      // and those handlers are what post the selected add-on products as their
      // own cart lines. Submitting the form directly went through the theme's
      // <product-form> handler instead, which knows only about the main product:
      // the add-on ended up as a line item property with no product and no price.
      function delegateAdd() {
        // Resolve now, not at startup: this is Globo's clone once it exists.
        var target = atcButton();
        // That button remains the authority on sold-out / unchosen variant.
        if (!target || isDisabled(target)) return;

        hidePopupError();

        // Close first: a modal <dialog> makes the rest of the page inert, and
        // the add has to run on the page exactly as it would without the popup.
        closePopup();
        // Then hand the Globo form back to the app's own DOM position, visible,
        // so the DOM Globo sees is byte-for-byte the one it sees without the
        // popup at all -- no extra difference left to explain a failure.
        restoreGpo();
        showGpo();

        // Armed before the click, so the watcher is patched in before Globo
        // fires its request. Anything but the theme's own button means Globo's
        // clone, i.e. an add the theme will never know about.
        armCartSync(target === nativeBtn);

        // Reset immediately before the click; the watcher flips it back on as
        // soon as a /cart/add is fired from inside it.
        addStarted = false;
        bypass = true;
        try {
          target.click();
        } finally {
          // Released on the next tick: the resulting submit and Globo's own
          // handlers are dispatched synchronously inside the click above.
          setTimeout(function () { bypass = false; }, 0);
        }

        // Globo checks its required options inside that click and, when one is
        // missing, writes its error into its own form and posts nothing. We had
        // just closed the popup and put that form back on the page, so the
        // customer saw the popup vanish and the form reappear inline with the
        // error in it. No request started => the add was refused: take the form
        // and its error back into the popup and reopen. Done synchronously, in
        // the same task as the close above, so the closed state is never
        // painted -- from the customer's side the popup simply stayed open.
        if (!addStarted) {
          refuseAdd();
        } else {
          // The add is on its way and the popup is closed: put the form back out
          // of sight. It was shown just above only so Globo would see the exact
          // DOM it sees without the popup; left visible, it stayed on the product
          // page behind the cart drawer until the next relocate() (i.e. the next
          // add-to-cart click). Done synchronously, in the same task as the click
          // and Globo's now-finished handlers, so the visible state is never
          // painted.
          hideAtHome();
        }
      }

      // The add was refused before it ever reached the network. Bring back the
      // popup around the form Globo just flagged, and add a message of our own:
      // the app writes its errors inside its form (which we move), but this
      // guarantees the customer is told why nothing happened even if a future
      // version of the app renders that message somewhere we do not move.
      // The cart sync stays armed on purpose: it costs nothing while no
      // /cart/add lands, and it still covers an app that would validate
      // asynchronously and post after this check.
      function refuseAdd() {
        var origin = lastFocus; // what opened the popup the first time
        openPopup();
        lastFocus = origin || lastFocus;
        showPopupError();
        var firstError = firstVisibleError();
        if (firstError && firstError.scrollIntoView) {
          try { firstError.scrollIntoView({ block: 'center' }); } catch (e) {}
        }
      }
      if (addBtn) {
        addBtn.addEventListener('click', function (e) {
          e.preventDefault();
          delegateAdd();
        });
      }

      // "Passer cette étape" (skip): add to cart WITHOUT the Globo options, so
      // it deliberately does NOT go through delegateAdd() -- there is no add-on
      // to preserve and Globo must not block on empty required fields. We disable every
      // field inside the Globo container so the theme's form serializer excludes
      // them (it skips disabled fields) and no option properties are added, then
      // call the theme's <product-form> add handler DIRECTLY. Calling it directly
      // (instead of submitting the form) bypasses Globo's own submit listener, so
      // empty required options can't block the add. The theme handler refreshes
      // the cart and dispatches 'add-to-cart', which closes the popup below.
      var skipBtn = popup.querySelector('[data-globo-popup-skip]');
      if (skipBtn) {
        skipBtn.addEventListener('click', function (e) {
          e.preventDefault();

          var container = gpoEl || (body && body.querySelector(GPO_SELECTOR));
          var toggled = [];
          if (container) {
            container.querySelectorAll('input, select, textarea').forEach(function (el) {
              if (!el.disabled) { el.disabled = true; toggled.push(el); }
            });
          }

          // We drive the loader ourselves: addBtn stays type="button" so it can
          // never submit the form behind Globo's back.
          if (addBtn) addBtn.classList.add('working');

          var pf = document.getElementById('add-to-cart-' + SECTION_ID);
          if (pf && typeof pf.onSubmitHandler === 'function') {
            pf.onSubmitHandler({ preventDefault: function () {} });
          } else if (addBtn) {
            addBtn.click(); // fallback: normal submit path
          }

          // Safety: re-enable the option fields (e.g. if the add failed).
          setTimeout(function () {
            toggled.forEach(function (el) { el.disabled = false; });
          }, 2500);
        });
      }

      // Editing anything in the popup clears our message: the app clears its
      // own inline errors the same way, so both disappear together.
      popup.addEventListener('input', hidePopupError);
      popup.addEventListener('change', hidePopupError);

      // Close interactions. A click on the ::backdrop is dispatched on the
      // <dialog> element itself; the dialog has no padding, so its own box is
      // fully covered by header/body/footer and can only be the target when the
      // click actually landed outside the dialog.
      popup.addEventListener('click', function (e) {
        if (e.target.closest('[data-globo-popup-close]') || e.target === dialog) closePopup();
      });
      // Escape is native on a modal dialog; this only covers the legacy
      // no-<dialog> fallback above.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) closePopup();
      });

      // Close the popup after a successful add-to-cart. The theme dispatches a
      // non-bubbling 'add-to-cart' event on the <product-form> element itself.
      // That event also means the theme handled the add end to end, so the
      // drawer sync above has nothing left to do.
      var pf = document.getElementById('add-to-cart-' + SECTION_ID);
      if (pf) {
        pf.addEventListener('add-to-cart', function () {
          cancelCartSync();
          closePopup();
        });
      }
    }
  }

  if (typeof customElements.get('globo-options-popup') === 'undefined') {
    customElements.define('globo-options-popup', GloboOptionsPopup);
  }
}
